/**
 * Collisions.ts — shared 2D boat footprint collision resolution.
 *
 * Boats are approximated by horizontal footprints in body XZ. For scene-mode
 * GLB boats each footprint is sampled from the object named Hull only, using
 * local mesh half-beams along body +X; boats without a named Hull part fall
 * back to a parametric outline. Runtime collision stays 2D SAT, but boat↔boat
 * and boat↔AABB contacts choose a footprint layer from the vertical contact
 * band so high/low obstacles don't all hit the same topsides outline.
 *
 * Resolution strategy (positional + velocity clamp; no bounce):
 *   1. Detect overlap.
 *   2. Push the colliders apart along the contact normal, weighted by mass
 *      (static obstacles have effectively infinite mass).
 *   3. Cancel the *approaching* component of world-frame velocity along the
 *      normal. Tangential motion is preserved so a boat can grind along a
 *      pier instead of getting stuck.
 *
 * Yaw torque from off-CG impacts is intentionally omitted: we want gentle
 * "you bumped the dock" behaviour, not violent spins. Player-induced
 * rotation comes from rudder + bow thruster as before.
 */

import type { Boat } from "./Boat";

export interface Aabb {
  /** World min corner (x, y, z). */
  min: readonly [number, number, number];
  /** World max corner (x, y, z). */
  max: readonly [number, number, number];
  /** Optional hook for soft obstacles that react when a boat pushes them. */
  onBoatImpact?: (impact: AabbImpact) => void;
  /**
   * Semantic tag for the impact event surfaced to the player. Defaults
   * to "dock" for the static pier / building obstacles. Buoy AABBs set
   * this to "buoy" so the practice scorer can choose to ignore those
   * contacts (e.g. mooring-to-buoy) or penalise them (slalom / circle).
   */
  kind?: "dock" | "buoy" | "embankment" | "powerline" | "bridge" | "obstacle";
}

export interface AabbImpact {
  boat: Boat;
  /** Contact normal from the AABB toward the boat, in world XZ. */
  normalX: number;
  normalZ: number;
  /** Positional overlap resolved this frame, in metres. */
  penetration: number;
  /** Closing speed along the contact normal, in m/s. */
  speed: number;
}

/**
 * Build an axis-aligned box from a centre + size triple. Convenience for
 * static dock geometry (which is created the same way).
 */
export function aabbFromBox(
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
): Aabb {
  const hx = sx * 0.5;
  const hy = sy * 0.5;
  const hz = sz * 0.5;
  return {
    min: [cx - hx, cy - hy, cz - hz],
    max: [cx + hx, cy + hy, cz + hz],
  };
}

/**
 * Yaw-rotated bounding box in world space. For procedural obstacles that
 * have a clear local frame (canal embankment running diagonally, slanted
 * jetties...), an OBB fits the geometry far tighter than an AABB whose
 * enclosing rectangle blows up by sqrt(2)× on a 45° diagonal.
 *
 * Pose: local +X is the "length" axis (e.g. along the wall), local +Z is
 * the "width" axis (e.g. across the wall). `rotY` is the heading of the
 * local +X axis in world XZ (radians, CCW from world +X).
 */
export interface Obb {
  /** World-space center (x, y, z). */
  center: readonly [number, number, number];
  /** Half-extents along local axes (length, height, width). */
  halfExtents: readonly [number, number, number];
  /** Yaw of the local +X axis around world Y, radians. */
  rotY: number;
  onBoatImpact?: (impact: AabbImpact) => void;
  kind?: Aabb["kind"];
}

/** Return the 4 world-space corners of an OBB at y = 0 (XZ outline). */
export function obbOutline2D(obb: Obb): BoatCollisionPoint2D[] {
  const c = Math.cos(obb.rotY);
  const s = Math.sin(obb.rotY);
  const cx = obb.center[0];
  const cz = obb.center[2];
  const hx = obb.halfExtents[0];
  const hz = obb.halfExtents[2];
  const xs = [-hx, +hx, +hx, -hx];
  const zs = [-hz, -hz, +hz, +hz];
  const out: BoatCollisionPoint2D[] = [];
  for (let i = 0; i < 4; i++) {
    const lx = xs[i]!;
    const lz = zs[i]!;
    out.push({ x: cx + lx * c - lz * s, z: cz + lx * s + lz * c });
  }
  return out;
}

/** Return the vertical span of an OBB (Y is world-up, OBBs are yaw-only). */
export function obbYRange(obb: Obb): { min: number; max: number } {
  const cy = obb.center[1];
  const hy = obb.halfExtents[1];
  return { min: cy - hy, max: cy + hy };
}

export interface BoatCollisionPoint2D {
  /** Body or world X coordinate, depending on the caller. */
  x: number;
  /** Body or world Z coordinate, depending on the caller. */
  z: number;
}

export interface BoatCollisionProfile {
  kind: "mesh-footprint" | "parametric-footprint";
  /** Number of outline vertices used by all runtime collision paths. */
  sampleCount: number;
  /** Contact skin scale applied after sampling the visible mesh. */
  skinScale: number;
  xMin: number;
  xMax: number;
  halfLength: number;
  halfBeam: number;
  /** GLB scene part names used to derive the footprint. Empty for fallback. */
  sourceParts: readonly string[];
  /** Clockwise/counter-clockwise body-frame outline, +X bow, +Z starboard. */
  outline: readonly BoatCollisionPoint2D[];
}

interface CollisionVertex3D {
  x: number;
  y: number;
  z: number;
}

interface BoatCollisionSource {
  length: number;
  beam: number;
  draftEq?: number;
  freeboard?: number;
  customMesh?: {
    vertices: Float32Array;
  };
  customScene?: {
    parts: ReadonlyArray<{
      name: string;
      verticesPbr: Float32Array;
      mesh: { indices: ArrayLike<number> };
      pivotPosition: readonly [number, number, number];
    }>;
  };
  namedAnchors?: Record<string, [number, number, number]>;
}

const BOAT_COLLISION_STATIONS = 49;
const BOAT_COLLISION_MIN_PROFILE_POINTS = 18;
const BOAT_COLLISION_SKIN_SCALE = 0.96;
const BOAT_COLLISION_LAYER_SKIN_SCALE = 1.0;
const BOAT_COLLISION_MIN_HALF_BEAM = 0.06;
const PROFILE_CACHE = new WeakMap<object, BoatCollisionProfile>();
const LAYER_CACHE = new WeakMap<object, readonly BoatCollisionLayer[]>();

interface BoatCollisionLayerOptions {
  requireMarkers?: boolean;
}

export interface BoatCollisionLayer {
  /** Local body-Y band represented by this profile. */
  yMin: number;
  yMax: number;
  /** Local body-Y height where the diagnostic cross-section is drawn. */
  sampleY: number;
  /** How the layer height was chosen. */
  source: "marker" | "fallback";
  profile: BoatCollisionProfile;
}

export type BoatCollisionLayerKey = "waterline" | "mid" | "gunwale";

export interface BoatCollisionLayerMarkerDiagnostic {
  key: BoatCollisionLayerKey;
  label: string;
  requiredNames: readonly string[];
  foundName?: string;
  position?: readonly [number, number, number];
  y?: number;
}

interface BoatCollisionBand {
  yMin: number;
  yMax: number;
  sampleY: number;
  source: "marker" | "fallback";
}

interface MeshCollisionProfileOptions {
  skinScale?: number;
}

const COLLISION_LAYER_MARKERS: ReadonlyArray<{
  key: BoatCollisionLayerKey;
  label: string;
  names: readonly string[];
}> = [
  {
    key: "waterline",
    label: "linia wodna",
    names: [
      "Collision.Waterline",
      "Collision.Hull.Waterline",
      "Hull.Collision.Waterline",
    ],
  },
  {
    key: "mid",
    label: "połowa burty",
    names: [
      "Collision.Mid",
      "Collision.Hull.Mid",
      "Hull.Collision.Mid",
      "Collision.Topsides.Mid",
    ],
  },
  {
    key: "gunwale",
    label: "rant burty",
    names: [
      "Collision.Gunwale",
      "Collision.Hull.Gunwale",
      "Hull.Collision.Gunwale",
      "Collision.DeckEdge",
      "Collision.Deck.Edge",
    ],
  },
];

export function getBoatCollisionProfile(
  source: Boat | BoatCollisionSource,
): BoatCollisionProfile {
  const dims = "params" in source ? source.params : source;
  const cached = PROFILE_CACHE.get(dims);
  if (cached) return cached;
  const range = collisionLocalYRange(dims);
  const meshProfile = buildMeshCollisionProfile(dims, range.yMin, range.yMax);
  const profile = meshProfile ?? buildParametricCollisionProfile(dims);
  PROFILE_CACHE.set(dims, profile);
  return profile;
}

export function getBoatCollisionLayers(
  source: Boat | BoatCollisionSource,
  options: BoatCollisionLayerOptions = {},
): readonly BoatCollisionLayer[] {
  const dims = "params" in source ? source.params : source;
  if (options.requireMarkers) assertBoatCollisionLayerMarkers(dims);
  const cached = LAYER_CACHE.get(dims);
  if (cached) return cached;
  const fallback = getBoatCollisionProfile(dims);
  const bands = collisionLayerBands(dims);
  const layers = bands.map((band) => ({
    yMin: band.yMin,
    yMax: band.yMax,
    sampleY: band.sampleY,
    source: band.source,
    profile:
      buildMeshCollisionProfile(dims, band.yMin, band.yMax, {
        skinScale: BOAT_COLLISION_LAYER_SKIN_SCALE,
      }) ?? fallback,
  }));
  LAYER_CACHE.set(dims, layers);
  return layers;
}

export function getBoatCollisionLayerMarkerDiagnostics(
  source: Boat | BoatCollisionSource,
): BoatCollisionLayerMarkerDiagnostic[] {
  const dims = "params" in source ? source.params : source;
  const anchors = dims.namedAnchors;
  return COLLISION_LAYER_MARKERS.map((spec) => {
    const match = anchors ? findAnchor(anchors, spec.names) : null;
    return {
      key: spec.key,
      label: spec.label,
      requiredNames: spec.names,
      ...(match
        ? {
            foundName: match.name,
            position: match.position,
            y: match.position[1],
          }
        : {}),
    };
  });
}

export function missingBoatCollisionLayerMarkers(
  source: Boat | BoatCollisionSource,
): BoatCollisionLayerMarkerDiagnostic[] {
  return getBoatCollisionLayerMarkerDiagnostics(source).filter(
    (marker) => !marker.foundName,
  );
}

export function assertBoatCollisionLayerMarkers(
  source: Boat | BoatCollisionSource,
): void {
  const missing = missingBoatCollisionLayerMarkers(source);
  if (missing.length === 0) return;
  const details = missing
    .map(
      (marker) =>
        `${marker.label}: add one of ${marker.requiredNames.join(" / ")}`,
    )
    .join("; ");
  throw new Error(`Missing player boat collision layer markers: ${details}`);
}

function collisionLocalYRange(dims: BoatCollisionSource): {
  yMin: number;
  yMax: number;
} {
  const freeboard = Math.max(0.45, dims.freeboard ?? dims.length * 0.08);
  return {
    // Runtime contact with docks and other boats happens against topsides,
    // not the submerged canoe body. Keep the diagnostic/runtime layers from
    // the waterline upward so the service view shows the useful sections.
    yMin: 0,
    yMax: freeboard + 0.25,
  };
}

function collisionLayerBands(dims: BoatCollisionSource): BoatCollisionBand[] {
  const range = collisionLocalYRange(dims);
  const freeboard = Math.max(0.45, dims.freeboard ?? dims.length * 0.08);
  const bandHalf = Math.max(0.08, Math.min(0.16, freeboard * 0.11));
  const markers = collisionLayerMarkers(dims);
  const maxBeamY =
    pickWidestHullSampleY(dims, 0, freeboard * 0.72, bandHalf) ??
    freeboard * 0.5;
  const makeBand = (
    sampleY: number,
    halfWidth: number,
    source: "marker" | "fallback",
  ): BoatCollisionBand => ({
    sampleY,
    yMin: Math.max(range.yMin, sampleY - halfWidth),
    yMax: Math.min(range.yMax, sampleY + halfWidth),
    source,
  });
  const candidates = [
    // Waterline: contacts low on the topsides.
    makeBand(
      markers.waterline ?? 0,
      bandHalf,
      markers.waterline == null ? "fallback" : "marker",
    ),
    // Mid topsides: halfway up to the widest usable hull section.
    makeBand(
      markers.mid ?? maxBeamY * 0.5,
      bandHalf,
      markers.mid == null ? "fallback" : "marker",
    ),
    // Upper collision section: never above the widest Hull-only outline.
    makeBand(
      markers.gunwale ?? maxBeamY,
      bandHalf,
      markers.gunwale == null ? "fallback" : "marker",
    ),
  ];
  const bands = candidates.filter((band) => band.yMax - band.yMin > 0.05);
  return bands.length > 0
    ? bands
    : [{ ...range, sampleY: range.yMax, source: "fallback" }];
}

function collisionLayerMarkers(dims: BoatCollisionSource): {
  waterline?: number;
  mid?: number;
  gunwale?: number;
} {
  const result: { waterline?: number; mid?: number; gunwale?: number } = {};
  for (const marker of getBoatCollisionLayerMarkerDiagnostics(dims)) {
    if (marker.y !== undefined) result[marker.key] = marker.y;
  }
  return result;
}

function findAnchor(
  anchors: Readonly<Record<string, [number, number, number]>>,
  names: readonly string[],
): { name: string; position: [number, number, number] } | null {
  const wanted = new Set(names.map(normalizeAnchorName));
  for (const [name, pos] of Object.entries(anchors)) {
    if (wanted.has(normalizeAnchorName(name))) {
      return { name, position: pos };
    }
  }
  return null;
}

function normalizeAnchorName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[._\-\s]+/g, ".");
}

function pickWidestHullSampleY(
  dims: BoatCollisionSource,
  yMin: number,
  yMax: number,
  halfWidth: number,
): number | null {
  if (!dims.customScene || yMax <= yMin) return null;
  let bestY = yMin;
  let bestHalfBeam = -Infinity;
  const samples = 13;
  for (let i = 0; i < samples; i++) {
    const sampleY = yMin + ((yMax - yMin) * i) / (samples - 1);
    const profile = buildMeshCollisionProfile(
      dims,
      sampleY - halfWidth,
      sampleY + halfWidth,
      { skinScale: BOAT_COLLISION_LAYER_SKIN_SCALE },
    );
    if (!profile) continue;
    if (profile.halfBeam > bestHalfBeam) {
      bestHalfBeam = profile.halfBeam;
      bestY = sampleY;
    }
  }
  return Number.isFinite(bestHalfBeam) ? bestY : null;
}

function boatCollisionWorldYRange(boat: Boat): { yMin: number; yMax: number } {
  const range = collisionLocalYRange(boat.params);
  return {
    yMin: boat.posY + range.yMin,
    yMax: boat.posY + range.yMax,
  };
}

function rangesOverlap(
  aMin: number,
  aMax: number,
  bMin: number,
  bMax: number,
): boolean {
  return Math.min(aMax, bMax) > Math.max(aMin, bMin);
}

function selectBoatCollisionProfileForWorldBand(
  boat: Boat,
  worldYMin: number,
  worldYMax: number,
  requireMarkers = false,
): BoatCollisionProfile {
  const localYMin = worldYMin - boat.posY;
  const localYMax = worldYMax - boat.posY;
  let best: BoatCollisionLayer | null = null;
  let bestOverlap = -Infinity;
  for (const layer of getBoatCollisionLayers(boat, { requireMarkers })) {
    const overlap =
      Math.min(localYMax, layer.yMax) - Math.max(localYMin, layer.yMin);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = layer;
    }
  }
  return best?.profile ?? getBoatCollisionProfile(boat);
}

function buildParametricCollisionProfile(
  dims: BoatCollisionSource,
): BoatCollisionProfile {
  const halfLength =
    Math.max(0.1, dims.length * 0.5) * BOAT_COLLISION_SKIN_SCALE;
  const halfBeam = Math.max(0.1, dims.beam * 0.5) * BOAT_COLLISION_SKIN_SCALE;
  const outline: BoatCollisionPoint2D[] = [];
  const sampleCount = 32;
  for (let i = 0; i < sampleCount; i++) {
    const a = (i / sampleCount) * Math.PI * 2;
    outline.push({
      x: Math.cos(a) * halfLength,
      z: Math.sin(a) * halfBeam,
    });
  }
  return {
    kind: "parametric-footprint",
    sampleCount,
    skinScale: BOAT_COLLISION_SKIN_SCALE,
    xMin: -halfLength,
    xMax: halfLength,
    halfLength,
    halfBeam,
    sourceParts: [],
    outline,
  };
}

function isHullCollisionPartName(name: string): boolean {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[._\-\s]+/g, ".");
  return /^hull(\.|$)/.test(normalized);
}

function buildMeshCollisionProfile(
  dims: BoatCollisionSource,
  yMin: number,
  yMax: number,
  options: MeshCollisionProfileOptions = {},
): BoatCollisionProfile | null {
  const skinScale = options.skinScale ?? BOAT_COLLISION_SKIN_SCALE;
  const maxAllowedHalfBeam = Math.max(0.2, dims.beam * 0.75);
  const pointsInBand: BoatCollisionPoint2D[] = [];
  const planePoints: BoatCollisionPoint2D[] = [];
  const sampleY = (yMin + yMax) * 0.5;
  const usePlaneSection = yMax - yMin <= 0.4;
  const addPoint = (
    target: BoatCollisionPoint2D[],
    x: number,
    z: number,
  ): void => {
    const halfBeam = Math.abs(z);
    if (halfBeam > maxAllowedHalfBeam) return;
    target.push({ x, z });
  };
  const addBandVertex = (x: number, y: number, z: number): void => {
    if (y < yMin || y > yMax) return;
    addPoint(pointsInBand, x, z);
  };
  const readVertex = (
    vertices: Float32Array,
    pivot: readonly [number, number, number],
    vertexIndex: number,
  ): CollisionVertex3D | null => {
    const base = vertexIndex * 11;
    if (base + 2 >= vertices.length) return null;
    return {
      x: vertices[base]! + pivot[0],
      y: vertices[base + 1]! + pivot[1],
      z: vertices[base + 2]! + pivot[2],
    };
  };
  const addPlaneEdgeIntersection = (
    a: CollisionVertex3D,
    b: CollisionVertex3D,
  ): void => {
    const da = a.y - sampleY;
    const db = b.y - sampleY;
    const eps = 1e-5;
    if (Math.abs(da) <= eps && Math.abs(db) <= eps) {
      addPoint(planePoints, a.x, a.z);
      addPoint(planePoints, b.x, b.z);
      return;
    }
    if (da * db > 0 || Math.abs(a.y - b.y) <= eps) return;
    const t = (sampleY - a.y) / (b.y - a.y);
    if (t < -eps || t > 1 + eps) return;
    addPoint(planePoints, a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t);
  };

  let sourceParts: string[] = [];
  if (dims.customScene) {
    const hullParts = dims.customScene.parts.filter((part) =>
      isHullCollisionPartName(part.name),
    );
    if (hullParts.length === 0) return null;
    sourceParts = hullParts.map((part) => part.name);
    for (const part of hullParts) {
      const v = part.verticesPbr;
      const piv = part.pivotPosition;
      for (let i = 0; i + 2 < v.length; i += 11) {
        addBandVertex(v[i]! + piv[0], v[i + 1]! + piv[1], v[i + 2]! + piv[2]);
      }
      if (usePlaneSection) {
        const indices = part.mesh.indices;
        for (let i = 0; i + 2 < indices.length; i += 3) {
          const a = readVertex(v, piv, indices[i]!);
          const b = readVertex(v, piv, indices[i + 1]!);
          const c = readVertex(v, piv, indices[i + 2]!);
          if (!a || !b || !c) continue;
          addPlaneEdgeIntersection(a, b);
          addPlaneEdgeIntersection(b, c);
          addPlaneEdgeIntersection(c, a);
        }
      }
    }
  } else {
    return null;
  }

  const points =
    usePlaneSection && planePoints.length >= BOAT_COLLISION_MIN_PROFILE_POINTS
      ? planePoints
      : pointsInBand;
  if (points.length < BOAT_COLLISION_MIN_PROFILE_POINTS) return null;

  let xMin = Infinity;
  let xMax = -Infinity;
  for (const p of points) {
    if (p.x < xMin) xMin = p.x;
    if (p.x > xMax) xMax = p.x;
  }
  if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMax - xMin < 1) {
    return null;
  }

  const halfBeams = new Float32Array(BOAT_COLLISION_STATIONS);
  const range = xMax - xMin;
  for (const p of points) {
    const idx = Math.max(
      0,
      Math.min(
        BOAT_COLLISION_STATIONS - 1,
        Math.round(((p.x - xMin) / range) * (BOAT_COLLISION_STATIONS - 1)),
      ),
    );
    const halfBeam = Math.abs(p.z);
    if (halfBeam > halfBeams[idx]!) halfBeams[idx] = halfBeam;
  }
  fillEmptyHalfBeamStations(halfBeams);

  const outline: BoatCollisionPoint2D[] = [];
  let maxHalfBeam = 0;
  for (let i = BOAT_COLLISION_STATIONS - 1; i >= 0; i--) {
    const x = xMin + (range * i) / (BOAT_COLLISION_STATIONS - 1);
    const rawHalfBeam = halfBeams[i]! * skinScale;
    const halfBeam =
      rawHalfBeam > 1e-5
        ? Math.max(BOAT_COLLISION_MIN_HALF_BEAM, rawHalfBeam)
        : 0;
    if (halfBeam > maxHalfBeam) maxHalfBeam = halfBeam;
    outline.push({ x, z: -halfBeam });
  }
  for (let i = 0; i < BOAT_COLLISION_STATIONS; i++) {
    const x = xMin + (range * i) / (BOAT_COLLISION_STATIONS - 1);
    const rawHalfBeam = halfBeams[i]! * skinScale;
    const halfBeam =
      rawHalfBeam > 1e-5
        ? Math.max(BOAT_COLLISION_MIN_HALF_BEAM, rawHalfBeam)
        : 0;
    outline.push({ x, z: halfBeam });
  }

  if (maxHalfBeam <= 0) return null;

  return {
    kind: "mesh-footprint",
    sampleCount: outline.length,
    skinScale,
    xMin,
    xMax,
    halfLength: Math.max(Math.abs(xMin), Math.abs(xMax)),
    halfBeam: maxHalfBeam,
    sourceParts,
    outline,
  };
}

function fillEmptyHalfBeamStations(halfBeams: Float32Array): void {
  const nonZero: number[] = [];
  for (let i = 0; i < halfBeams.length; i++) {
    if (halfBeams[i]! > 0) nonZero.push(i);
  }
  if (nonZero.length === 0) return;

  for (let n = 0; n < nonZero.length - 1; n++) {
    const i0 = nonZero[n]!;
    const i1 = nonZero[n + 1]!;
    if (i1 <= i0 + 1) continue;
    const v0 = halfBeams[i0]!;
    const v1 = halfBeams[i1]!;
    for (let j = i0 + 1; j < i1; j++) {
      const a = (j - i0) / (i1 - i0);
      halfBeams[j] = v0 * (1 - a) + v1 * a;
    }
  }

  const first = nonZero[0]!;
  if (first > 0) {
    const v = halfBeams[first]!;
    for (let j = 0; j < first; j++) {
      halfBeams[j] = v * (j / first);
    }
  }

  const last = nonZero[nonZero.length - 1]!;
  const end = halfBeams.length - 1;
  if (last < end) {
    const v = halfBeams[last]!;
    for (let j = last + 1; j <= end; j++) {
      halfBeams[j] = v * ((end - j) / (end - last));
    }
  }
}

export function getBoatCollisionWorldOutline(
  boat: Boat,
  profile = getBoatCollisionProfile(boat),
): BoatCollisionPoint2D[] {
  return profile.outline.map((p) => bodyToWorld2D(boat, p));
}

export function projectBodyPointOutsideBoatCollisionHull(
  source: Boat | BoatCollisionSource,
  point: [number, number, number],
  options: {
    /** Side used when a point is exactly on the centreline. +Z starboard. */
    preferredSide?: number;
    /** Keep contact on preferredSide instead of following tiny Z sign changes. */
    forceSide?: boolean;
    /** Use preferredSide while |Z| is smaller than this dead zone. */
    sideLockThreshold?: number;
    /** Clearance beyond the sampled Hull outline, in metres. */
    margin?: number;
    /** Extra vertical reach below the lowest collision layer. */
    belowWaterlineMargin?: number;
    /** Extra vertical reach above the highest collision layer. */
    aboveGunwaleMargin?: number;
    /** Extra longitudinal reach beyond bow/stern caps for rope contact. */
    longitudinalMargin?: number;
  } = {},
): boolean {
  const dims = "params" in source ? source.params : source;
  const layers = getBoatCollisionLayers(dims);
  if (layers.length === 0) return false;

  const margin = options.margin ?? 0.08;
  const belowWaterlineMargin =
    options.belowWaterlineMargin ?? Math.max(0.25, dims.draftEq ?? 0.4);
  const aboveGunwaleMargin =
    options.aboveGunwaleMargin ??
    Math.max(0.2, Math.min(0.45, (dims.freeboard ?? 0.8) * 0.45));
  const longitudinalMargin =
    options.longitudinalMargin ?? Math.max(0.35, margin * 4);
  let minY = Infinity;
  let maxY = -Infinity;
  for (const layer of layers) {
    minY = Math.min(minY, layer.yMin);
    maxY = Math.max(maxY, layer.yMax);
  }
  if (
    point[1] < minY - belowWaterlineMargin ||
    point[1] > maxY + aboveGunwaleMargin
  ) {
    return false;
  }

  const envelope = sampleCollisionEnvelopeAtBodyY(layers, point[1], point[0]);
  if (!envelope) return false;
  if (
    point[0] < envelope.xMin - longitudinalMargin ||
    point[0] > envelope.xMax + longitudinalMargin
  ) {
    return false;
  }
  const halfBeam = envelope.halfBeam;
  if (halfBeam <= BOAT_COLLISION_MIN_HALF_BEAM) return false;

  const expandedHalfBeam = halfBeam + margin;
  const absZ = Math.abs(point[2]);
  if (absZ >= expandedHalfBeam) return false;
  const sideLockThreshold = options.sideLockThreshold ?? 0.12;

  const sideDist = expandedHalfBeam - absZ;
  const bowDist = envelope.xMax + margin - point[0];
  const sternDist = point[0] - (envelope.xMin - margin);
  if (sideDist <= bowDist && sideDist <= sternDist) {
    const side =
      options.forceSide && options.preferredSide !== undefined
        ? Math.sign(options.preferredSide) || 1
        : absZ > sideLockThreshold
          ? Math.sign(point[2])
          : Math.sign(options.preferredSide ?? 0) || 1;
    point[2] = side * expandedHalfBeam;
  } else if (bowDist <= sternDist) {
    point[0] = envelope.xMax + margin;
  } else {
    point[0] = envelope.xMin - margin;
  }
  return true;
}

export function projectBodySegmentOutsideBoatCollisionHull(
  source: Boat | BoatCollisionSource,
  a: [number, number, number],
  b: [number, number, number],
  options: {
    /** Side used when the segment midpoint is exactly on the centreline. */
    preferredSide?: number;
    /** Keep contact on preferredSide instead of following tiny Z sign changes. */
    forceSide?: boolean;
    /** Use preferredSide while |Z| is smaller than this dead zone. */
    sideLockThreshold?: number;
    /** Clearance beyond the sampled Hull outline, in metres. */
    margin?: number;
    /** Extra vertical reach below the lowest collision layer. */
    belowWaterlineMargin?: number;
    /** Extra vertical reach above the highest collision layer. */
    aboveGunwaleMargin?: number;
    /** Extra longitudinal reach beyond bow/stern caps for rope contact. */
    longitudinalMargin?: number;
    /** Whether endpoint A can be moved by the correction. */
    moveA?: boolean;
    /** Whether endpoint B can be moved by the correction. */
    moveB?: boolean;
  } = {},
): boolean {
  const moveA = options.moveA ?? true;
  const moveB = options.moveB ?? true;
  if (!moveA && !moveB) return false;

  let changed = false;
  for (const t of [0.25, 0.5, 0.75]) {
    const invT = 1 - t;
    const sample: [number, number, number] = [
      a[0] * invT + b[0] * t,
      a[1] * invT + b[1] * t,
      a[2] * invT + b[2] * t,
    ];
    const projected: [number, number, number] = [
      sample[0],
      sample[1],
      sample[2],
    ];
    if (!projectBodyPointOutsideBoatCollisionHull(source, projected, options)) {
      continue;
    }

    let dx = projected[0] - sample[0];
    let dz = projected[2] - sample[2];
    const len = Math.hypot(dx, dz);
    if (len <= 1e-5) continue;
    const maxCorrection = 0.22;
    if (len > maxCorrection) {
      const s = maxCorrection / len;
      dx *= s;
      dz *= s;
    }

    let ax = 0;
    let az = 0;
    let bx = 0;
    let bz = 0;
    if (moveA && moveB) {
      const denom = invT * invT + t * t;
      ax = (invT / denom) * dx;
      az = (invT / denom) * dz;
      bx = (t / denom) * dx;
      bz = (t / denom) * dz;
    } else if (moveA) {
      ax = dx / invT;
      az = dz / invT;
    } else {
      bx = dx / t;
      bz = dz / t;
    }

    if (moveA) {
      a[0] += ax;
      a[2] += az;
    }
    if (moveB) {
      b[0] += bx;
      b[2] += bz;
    }
    changed = true;
  }
  return changed;
}

function sampleCollisionEnvelopeAtBodyY(
  layers: readonly BoatCollisionLayer[],
  y: number,
  x: number,
): { xMin: number; xMax: number; halfBeam: number } | null {
  if (layers.length === 0) return null;
  const sorted = [...layers].sort((a, b) => a.sampleY - b.sampleY);
  let lower = sorted[0]!;
  let upper = sorted[sorted.length - 1]!;
  let t = 0;

  if (y <= lower.sampleY) {
    upper = lower;
  } else if (y >= upper.sampleY) {
    lower = upper;
  } else {
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i]!;
      const b = sorted[i + 1]!;
      if (y >= a.sampleY && y <= b.sampleY) {
        lower = a;
        upper = b;
        t = (y - a.sampleY) / Math.max(1e-5, b.sampleY - a.sampleY);
        break;
      }
    }
  }

  const xMin =
    lower.profile.xMin + (upper.profile.xMin - lower.profile.xMin) * t;
  const xMax =
    lower.profile.xMax + (upper.profile.xMax - lower.profile.xMax) * t;
  const sampleX = Math.min(Math.max(x, xMin), xMax);
  const lowerHalfBeam = sampleProfileHalfBeamAtX(lower.profile, sampleX);
  const upperHalfBeam = sampleProfileHalfBeamAtX(upper.profile, sampleX);
  let halfBeam = 0;
  if (
    lowerHalfBeam > BOAT_COLLISION_MIN_HALF_BEAM &&
    upperHalfBeam > BOAT_COLLISION_MIN_HALF_BEAM
  ) {
    halfBeam = lowerHalfBeam + (upperHalfBeam - lowerHalfBeam) * t;
  } else {
    halfBeam = Math.max(lowerHalfBeam, upperHalfBeam);
  }

  return {
    xMin,
    xMax,
    halfBeam,
  };
}

function sampleProfileHalfBeamAtX(
  profile: BoatCollisionProfile,
  x: number,
): number {
  const outline = profile.outline;
  if (outline.length < 2) return 0;
  const eps = 1e-5;
  let halfBeam = 0;
  let closestD = Infinity;
  let closestHalfBeam = 0;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    if (x >= minX - eps && x <= maxX + eps) {
      const dx = b.x - a.x;
      if (Math.abs(dx) <= eps) {
        if (Math.abs(x - a.x) <= eps) {
          halfBeam = Math.max(halfBeam, Math.abs(a.z), Math.abs(b.z));
        }
      } else {
        const t = (x - a.x) / dx;
        if (t >= -eps && t <= 1 + eps) {
          const z = a.z + (b.z - a.z) * t;
          halfBeam = Math.max(halfBeam, Math.abs(z));
        }
      }
    }

    const d = Math.abs(a.x - x);
    if (d < closestD) {
      closestD = d;
      closestHalfBeam = Math.abs(a.z);
    }
  }
  return halfBeam > 0 ? halfBeam : closestHalfBeam;
}

function bodyToWorld2D(
  boat: Boat,
  p: BoatCollisionPoint2D,
): BoatCollisionPoint2D {
  const ch = Math.cos(boat.heading);
  const sh = Math.sin(boat.heading);
  const fwdX = sh;
  const fwdZ = -ch;
  const rgtX = ch;
  const rgtZ = sh;
  return {
    x: boat.posX + p.x * fwdX + p.z * rgtX,
    z: boat.posZ + p.x * fwdZ + p.z * rgtZ,
  };
}

interface Contact2D {
  /** Contact normal from B toward A. */
  nx: number;
  nz: number;
  penetration: number;
}

function polygonCenter(
  points: readonly BoatCollisionPoint2D[],
): BoatCollisionPoint2D {
  let x = 0;
  let z = 0;
  for (const p of points) {
    x += p.x;
    z += p.z;
  }
  const inv = points.length > 0 ? 1 / points.length : 0;
  return { x: x * inv, z: z * inv };
}

function projectPolygon(
  points: readonly BoatCollisionPoint2D[],
  ax: number,
  az: number,
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const v = p.x * ax + p.z * az;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

function testAxes(
  pointsA: readonly BoatCollisionPoint2D[],
  pointsB: readonly BoatCollisionPoint2D[],
  centerDeltaX: number,
  centerDeltaZ: number,
  best: Contact2D,
): boolean {
  for (let i = 0; i < pointsA.length; i++) {
    const p = pointsA[i]!;
    const q = pointsA[(i + 1) % pointsA.length]!;
    const ex = q.x - p.x;
    const ez = q.z - p.z;
    const len = Math.hypot(ex, ez);
    if (len < 1e-6) continue;
    let ax = -ez / len;
    let az = ex / len;
    if (centerDeltaX * ax + centerDeltaZ * az < 0) {
      ax = -ax;
      az = -az;
    }
    const aProj = projectPolygon(pointsA, ax, az);
    const bProj = projectPolygon(pointsB, ax, az);
    const overlap =
      Math.min(aProj.max, bProj.max) - Math.max(aProj.min, bProj.min);
    if (overlap <= 0) return false;
    if (overlap < best.penetration) {
      best.nx = ax;
      best.nz = az;
      best.penetration = overlap;
    }
  }
  return true;
}

function satConvexPolygons(
  pointsA: readonly BoatCollisionPoint2D[],
  pointsB: readonly BoatCollisionPoint2D[],
): Contact2D | null {
  const centerA = polygonCenter(pointsA);
  const centerB = polygonCenter(pointsB);
  const centerDeltaX = centerA.x - centerB.x;
  const centerDeltaZ = centerA.z - centerB.z;
  const best: Contact2D = { nx: 1, nz: 0, penetration: Infinity };
  if (!testAxes(pointsA, pointsB, centerDeltaX, centerDeltaZ, best)) {
    return null;
  }
  if (!testAxes(pointsB, pointsA, centerDeltaX, centerDeltaZ, best)) {
    return null;
  }
  return best;
}

function aabbOutline(box: Aabb): BoatCollisionPoint2D[] {
  return [
    { x: box.min[0], z: box.min[2] },
    { x: box.max[0], z: box.min[2] },
    { x: box.max[0], z: box.max[2] },
    { x: box.min[0], z: box.max[2] },
  ];
}

/**
 * Cancel the velocity component along world-frame normal (nx, nz) that is
 * pushing the boat *into* the obstacle. Tangential motion is preserved.
 */
function killApproach(boat: Boat, nx: number, nz: number): void {
  const ch = Math.cos(boat.heading);
  const sh = Math.sin(boat.heading);
  const fwdX = sh;
  const fwdZ = -ch;
  const rgtX = ch;
  const rgtZ = sh;
  const vx = boat.surge * fwdX + boat.sway * rgtX;
  const vz = boat.surge * fwdZ + boat.sway * rgtZ;
  const vn = vx * nx + vz * nz;
  if (vn >= 0) return; // already separating
  const nvx = vx - vn * nx;
  const nvz = vz - vn * nz;
  boat.surge = nvx * fwdX + nvz * fwdZ;
  boat.sway = nvx * rgtX + nvz * rgtZ;
}

/** A boat tagged as static is treated as infinite-mass (e.g. moored). */
export interface CollisionBody {
  boat: Boat;
  /** If true, the boat will not be displaced and its velocity is ignored. */
  immovable: boolean;
  /** Player-authored boats should fail loudly instead of using layer fallbacks. */
  requireCollisionLayerMarkers?: boolean;
}

/**
 * Impact event recorded by the resolution functions when a moving boat
 * actually closes onto an obstacle (i.e. the approaching velocity component
 * along the contact normal exceeds a small threshold). Light scrapes and
 * boats already separating do not produce events.
 *
 * `speed` is the closing speed at the contact, in m/s — useful for both
 * audio gain and pitch.
 */
export interface ImpactEvent {
  kind:
    | "dock"
    | "ground"
    | "boat"
    | "buoy"
    | "embankment"
    | "powerline"
    | "bridge"
    | "obstacle";
  boat: Boat;
  speed: number;
}

/** Below this closing speed (m/s) a contact is treated as a light scrape. */
const IMPACT_SPEED_MIN = 0.4;

/**
 * Returns the *closing* speed (positive = approaching) of the boat along
 * the world-frame normal (nx, nz). Negative result means the boat is
 * already separating along that normal.
 */
function approachSpeed(boat: Boat, nx: number, nz: number): number {
  const ch = Math.cos(boat.heading);
  const sh = Math.sin(boat.heading);
  const fwdX = sh;
  const fwdZ = -ch;
  const rgtX = ch;
  const rgtZ = sh;
  const vx = boat.surge * fwdX + boat.sway * rgtX;
  const vz = boat.surge * fwdZ + boat.sway * rgtZ;
  // For the dock case the normal points from the obstacle toward the boat,
  // so an approaching boat has a NEGATIVE projection (moving against n).
  return -(vx * nx + vz * nz);
}

/**
 * Generic mast ↔ static-AABB collision. Treats the mast as a line
 * segment from the deck-stepped foot at `(mast.offsetX, freeboard)`
 * to the tip — vertical when `boat.mastAngle == 0`, or rotated aft
 * by `mastAngle` rad on foldable-mast hulls. The segment is sampled
 * uniformly and tested against the box, so a folded mast lying over
 * the stern correctly hits bridges / overhead obstacles that the
 * vertical spar would have ducked under (and vice-versa).
 *
 * Early-exits when the segment's full Y range is outside the box,
 * so it is safe to call for every static AABB regardless of kind.
 */
function resolveMastBoxCollision(
  body: CollisionBody,
  box: Aabb,
  events?: ImpactEvent[],
): boolean {
  const boat = body.boat;
  const mast = boat.params.mast;
  if (!mast || mast.height <= 0) return false;

  const baseY = boat.posY + Math.max(0, boat.params.freeboard);
  const ang = boat.mastAngle;
  const baseBodyX = mast.offsetX;
  // Foldable masts tilt the tip aft (body −X) and lower it. For
  // ang = 0 this collapses to a pure vertical column — same envelope
  // as the legacy test.
  const tipBodyX = baseBodyX - mast.height * Math.sin(ang);
  const baseWorldY = baseY;
  const tipWorldY = baseY + mast.height * Math.cos(ang);

  const segMinY = Math.min(baseWorldY, tipWorldY);
  const segMaxY = Math.max(baseWorldY, tipWorldY);
  if (!rangesOverlap(segMinY, segMaxY, box.min[1], box.max[1])) {
    return false;
  }

  const radius = Math.max(0.12, mast.radius + 0.16);
  const minX = box.min[0] - radius;
  const maxX = box.max[0] + radius;
  const minZ = box.min[2] - radius;
  const maxZ = box.max[2] + radius;

  // Sample uniformly; vertical mast collapses to repeated foot
  // samples (same XZ), so the conservative push depth still matches
  // the legacy behaviour. 12 samples ≈ ≤1 m spacing for a 11 m spar.
  const SAMPLES = 12;
  let bestDepth = 0;
  let bestNx = 0;
  let bestNz = 0;
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const bx = baseBodyX + t * (tipBodyX - baseBodyX);
    const wy = baseWorldY + t * (tipWorldY - baseWorldY);
    if (wy < box.min[1] || wy > box.max[1]) continue;
    const p = bodyToWorld2D(boat, { x: bx, z: 0 });
    if (p.x < minX || p.x > maxX || p.z < minZ || p.z > maxZ) continue;
    const dxL = p.x - minX;
    const dxR = maxX - p.x;
    const dzN = p.z - minZ;
    const dzP = maxZ - p.z;
    let pen = dxL;
    let nx = -1;
    let nz = 0;
    if (dxR < pen) {
      pen = dxR;
      nx = 1;
      nz = 0;
    }
    if (dzN < pen) {
      pen = dzN;
      nx = 0;
      nz = -1;
    }
    if (dzP < pen) {
      pen = dzP;
      nx = 0;
      nz = 1;
    }
    if (pen > bestDepth) {
      bestDepth = pen;
      bestNx = nx;
      bestNz = nz;
    }
  }
  if (bestDepth <= 0) return false;

  const push = Math.min(bestDepth, 0.35);
  boat.posX += bestNx * push;
  boat.posZ += bestNz * push;
  const closing = approachSpeed(boat, bestNx, bestNz);
  box.onBoatImpact?.({
    boat,
    normalX: bestNx,
    normalZ: bestNz,
    penetration: bestDepth,
    speed: Math.max(0, closing),
  });
  if (events && closing > IMPACT_SPEED_MIN) {
    events.push({ kind: box.kind ?? "dock", boat, speed: closing });
  }
  killApproach(boat, bestNx, bestNz);
  boat.surge *= 0.9;
  boat.sway *= 0.9;
  boat.yawRate *= 0.8;
  return true;
}

/**
 * Resolve overlap of the shared boat footprint with land cells. Caller
 * supplies a predicate `isLand(x, z)` (true when the world-space point is on
 * land). The boat is pushed toward the nearest water cell; velocity component
 * pushing further into shore is cancelled.
 *
 * Decoupled from `TerrainAsset` so this module stays pure simulation.
 */
export function resolveTerrainShoreCollisions(
  bodies: readonly CollisionBody[],
  isLand: (x: number, z: number) => boolean,
  /** Maximum search radius in metres for the nearest water sample. */
  maxRadius = 30,
  /** Search step in metres. */
  step = 1.5,
  /** Optional sink for impact events generated this frame. */
  events?: ImpactEvent[],
): void {
  for (const body of bodies) {
    if (body.immovable) continue;
    const boat = body.boat;
    // Shore collision uses the WATERLINE-band hull outline, not the full
    // (deck-level) profile used for boat↔boat and boat↔dock. The deck is
    // typically wider than the waterline, so using the full profile would
    // stop the hull short of the visible shoreline by half a beam. The
    // waterline outline is what actually touches land at the water plane.
    const layers = getBoatCollisionLayers(boat);
    const profile = layers[0]?.profile ?? getBoatCollisionProfile(boat);
    const ch = Math.cos(boat.heading);
    const sh = Math.sin(boat.heading);
    const fwdX = sh;
    const fwdZ = -ch;
    const rgtX = ch;
    const rgtZ = sh;
    const bow = bodyToWorld2D(boat, { x: profile.halfLength, z: 0 });
    const stern = bodyToWorld2D(boat, { x: -profile.halfLength, z: 0 });
    const samples = getBoatCollisionWorldOutline(boat, profile);
    let corrX = 0;
    let corrZ = 0;
    let hits = 0;
    /** Longitudinal coord (relative to CG, +bow) of the deepest contact. */
    let deepestSampleX = 0;
    // Anticipatory braking: probe a short distance ahead in the direction
    // of motion. If we'd hit shore within `lookAhead` metres, scale down
    // surge/sway BEFORE integration moves us into land. This eliminates the
    // 1-frame penetration + corrective-jump artifact at high speed.
    //
    // Only engages above a moderate speed: at low speed the small
    // per-frame positional cap (0.5 m) already produces a smooth stop,
    // and a low-speed brake would slow the boat well before it reaches a
    // dock face that has land behind it (the shore line typically sits
    // right under or just past the dock).
    const speed = Math.hypot(boat.surge, boat.sway);
    if (speed > 1.0) {
      // World-frame velocity direction.
      const wvx = boat.surge * fwdX + boat.sway * rgtX;
      const wvz = boat.surge * fwdZ + boat.sway * rgtZ;
      const wvm = Math.hypot(wvx, wvz);
      if (wvm > 1e-3) {
        const dx = wvx / wvm;
        const dz = wvz / wvm;
        // Pick the end of the boat closer to the motion direction.
        // dotBow = (bow-CG) · velDir = halfLOA * (fwd · velDir)
        const fwdDotVel = fwdX * dx + fwdZ * dz;
        const origin = fwdDotVel >= 0 ? bow : stern;
        const lookAhead = Math.min(speed * 0.6, 4.0); // ~0.6s ahead, max 4m
        const probeX = origin.x + dx * lookAhead;
        const probeZ = origin.z + dz * lookAhead;
        if (isLand(probeX, probeZ)) {
          // Approximate distance to shore by sampling along the ray.
          let dShore = lookAhead;
          for (let s = step; s < lookAhead; s += step) {
            if (isLand(origin.x + dx * s, origin.z + dz * s)) {
              dShore = s;
              break;
            }
          }
          // Squared curve: brake harder when close, gentle when far.
          const t = dShore / lookAhead;
          boat.surge *= t * t;
          boat.sway *= t * t;
        }
      }
    }
    for (const sample of samples) {
      const sx = sample.x;
      const sz = sample.z;
      if (!isLand(sx, sz)) continue;
      // Local longitudinal coord of this sample relative to CG (positive = bow).
      const dxFromCG = sx - boat.posX;
      const dzFromCG = sz - boat.posZ;
      const sampleLocalX = dxFromCG * fwdX + dzFromCG * fwdZ;
      // Scan a disc around the contact point. Track BOTH the nearest
      // water sample (for penetration depth) AND a weighted centroid of
      // all water samples (for the push direction).
      //
      // The nearest-sample-only approach used previously caused the push
      // normal to snap between discrete angular bins as the boat moved,
      // producing visible shake when resting on shore. Averaging many
      // samples — weighted toward the closer ones — yields a smooth
      // normal that varies continuously with boat position.
      let foundX = 0;
      let foundZ = 0;
      let bestD2 = Infinity;
      let cwx = 0;
      let cwz = 0;
      let cwSum = 0;
      for (let r = step; r <= maxRadius; r += step) {
        const steps = Math.max(8, Math.ceil((2 * Math.PI * r) / step));
        for (let i = 0; i < steps; i++) {
          const a = (i / steps) * Math.PI * 2;
          const px = sx + Math.cos(a) * r;
          const pz = sz + Math.sin(a) * r;
          if (!isLand(px, pz)) {
            const dx = px - sx;
            const dz = pz - sz;
            const d2 = dx * dx + dz * dz;
            if (d2 < bestD2) {
              bestD2 = d2;
              foundX = px;
              foundZ = pz;
            }
            // 1/(r+step) weighting: near samples dominate but far ones
            // still smooth out single-sample flicker.
            const w = 1 / (r + step);
            cwx += dx * w;
            cwz += dz * w;
            cwSum += w;
          }
        }
      }
      if (bestD2 === Infinity) continue;
      const foundD = Math.sqrt(bestD2);
      if (foundD < 1e-5) continue;
      // Push direction = smoothed centroid; fall back to nearest sample
      // if the centroid is degenerate.
      let nx = (foundX - sx) / foundD;
      let nz = (foundZ - sz) / foundD;
      if (cwSum > 1e-9) {
        const ax = cwx / cwSum;
        const az = cwz / cwSum;
        const am = Math.hypot(ax, az);
        if (am > 1e-5) {
          nx = ax / am;
          nz = az / am;
        }
      }
      // Track the DEEPEST sample's correction (not average): when the bow
      // hits shore at an angle, one corner can be 2 m inland while the
      // opposite corner is barely scraping. Averaging leaves the deep
      // corner stuck. Take the largest push-out instead.
      // No skin offset — push exactly to the shore line. The skin caused
      // visible "snap-back" jumps on light contact.
      const pen = foundD;
      const px = nx * pen;
      const pz = nz * pen;
      if (px * px + pz * pz > corrX * corrX + corrZ * corrZ) {
        corrX = px;
        corrZ = pz;
        deepestSampleX = sampleLocalX;
      }
      hits++;
    }
    if (hits > 0) {
      // (corrX, corrZ) already holds the deepest sample's full push-out.
      // Only clamp the forward-component when the deepest contact is at
      // the BOW. For stern contact, the correction must push the boat
      // forward (away from shore behind us); blocking that would clip the
      // stern indefinitely.
      if (deepestSampleX > 0) {
        const alongFwd = corrX * fwdX + corrZ * fwdZ;
        if (alongFwd > 0) {
          corrX -= fwdX * alongFwd;
          corrZ -= fwdZ * alongFwd;
        }
      }
      // Baumgarte-style soft stabilization: each frame correct only a
      // fraction of the penetration. Spread over several frames this
      // converges smoothly without the visible "bounce" of pushing the
      // hull fully out in one step. Keep a small hard cap as a safety
      // net for huge initial overlaps (teleport, terrain swap).
      const cmRaw = Math.hypot(corrX, corrZ);
      let nxN = 0;
      let nzN = 0;
      if (cmRaw > 1e-5) {
        nxN = corrX / cmRaw;
        nzN = corrZ / cmRaw;
      }
      // "Skin" tolerance: when penetration is tiny AND the boat is not
      // actively moving inward, do absolutely nothing. This lets the
      // hull rest in light contact with shore without per-frame micro-
      // corrections that look like shake.
      const skin = 0.08;
      const closing = cmRaw > 1e-5 ? approachSpeed(boat, nxN, nzN) : 0;
      if (cmRaw < skin && closing < 0.05) {
        continue;
      }
      const baumgarte = 0.3;
      const maxCorr = 0.15;
      // Only correct the part of penetration that exceeds the skin —
      // keeps the boat in light contact instead of pushing it cleanly
      // off the shore each frame.
      const excess = Math.max(0, cmRaw - skin);
      let appliedMag = excess * baumgarte;
      if (appliedMag > maxCorr) appliedMag = maxCorr;
      const appliedX = nxN * appliedMag;
      const appliedZ = nzN * appliedMag;
      boat.posX += appliedX;
      boat.posZ += appliedZ;
      if (cmRaw > 1e-5) {
        if (events && closing > IMPACT_SPEED_MIN) {
          events.push({ kind: "ground", boat, speed: closing });
        }
        // Only cancel the inward velocity when there is meaningful
        // closing speed. At rest, killApproach would zero waves-driven
        // micro-motion every frame, which combined with new input next
        // frame is what produced the visible shake.
        if (closing > 0.05) {
          killApproach(boat, nxN, nzN);
        }
      }
      // Tangential friction scaled by how hard we're pressing in. Light
      // touch ⇒ almost no extra damping; firm grind ⇒ stronger braking.
      const press = Math.min(1, cmRaw / 0.4);
      const fSurge = 1 - 0.06 * press;
      const fSway = 1 - 0.14 * press;
      const fYaw = 1 - 0.3 * press;
      boat.surge *= fSurge;
      boat.sway *= fSway;
      boat.yawRate *= fYaw;
    }
  }
}

/**
 * Resolve all boat-boat and boat-AABB overlaps for one frame.
 * Call once per frame *after* all `Boat.step()` calls have completed.
 */
export function resolveCollisions(
  bodies: CollisionBody[],
  staticAabbs: readonly Aabb[],
  /** Optional sink for impact events generated this frame. */
  events?: ImpactEvent[],
  /**
   * Yaw-rotated static obstacles. Authored by procedural builders that
   * have a natural local frame (canal embankments, slanted jetties...).
   * Treated by the same boat-footprint SAT as `staticAabbs`, just with
   * rotated corners instead of min/max.
   */
  staticObbs: readonly Obb[] = [],
): void {
  // Boat ↔ boat (shared footprint SAT).
  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) {
      const A = bodies[i]!;
      const B = bodies[j]!;
      if (A.immovable && B.immovable) continue;
      const aY = boatCollisionWorldYRange(A.boat);
      const bY = boatCollisionWorldYRange(B.boat);
      if (!rangesOverlap(aY.yMin, aY.yMax, bY.yMin, bY.yMax)) continue;
      const contactYMin = Math.max(aY.yMin, bY.yMin);
      const contactYMax = Math.min(aY.yMax, bY.yMax);
      const profileA = selectBoatCollisionProfileForWorldBand(
        A.boat,
        contactYMin,
        contactYMax,
        !!A.requireCollisionLayerMarkers,
      );
      const profileB = selectBoatCollisionProfileForWorldBand(
        B.boat,
        contactYMin,
        contactYMax,
        !!B.requireCollisionLayerMarkers,
      );
      const contact = satConvexPolygons(
        getBoatCollisionWorldOutline(A.boat, profileA),
        getBoatCollisionWorldOutline(B.boat, profileB),
      );
      if (!contact) continue;
      const pen = contact.penetration;
      const nx = contact.nx;
      const nz = contact.nz;
      // Mass-weighted positional correction. Cap the per-frame push so
      // overlapping boats ease apart over multiple frames instead of
      // teleporting (which reads as an unnatural bounce).
      const ma = A.immovable ? Infinity : A.boat.params.mass;
      const mb = B.immovable ? Infinity : B.boat.params.mass;
      const wa =
        isFinite(ma) && isFinite(mb) ? mb / (ma + mb) : A.immovable ? 0 : 1;
      const wb = 1 - wa;
      const maxBoatCorr = 0.5;
      const pushTotal = Math.min(pen, maxBoatCorr);
      if (!A.immovable) {
        A.boat.posX += nx * pushTotal * wa;
        A.boat.posZ += nz * pushTotal * wa;
        if (events) {
          const closing = approachSpeed(A.boat, nx, nz);
          if (closing > IMPACT_SPEED_MIN) {
            events.push({
              kind: B.immovable ? "dock" : "boat",
              boat: A.boat,
              speed: closing,
            });
          }
        }
        killApproach(A.boat, nx, nz);
      }
      if (!B.immovable) {
        B.boat.posX -= nx * pushTotal * wb;
        B.boat.posZ -= nz * pushTotal * wb;
        if (events) {
          const closing = approachSpeed(B.boat, -nx, -nz);
          if (closing > IMPACT_SPEED_MIN) {
            events.push({
              kind: A.immovable ? "dock" : "boat",
              boat: B.boat,
              speed: closing,
            });
          }
        }
        killApproach(B.boat, -nx, -nz);
      }
    }
  }

  // Boat ↔ static AABB (shared footprint vs box SAT).
  for (const body of bodies) {
    if (body.immovable) continue;
    for (const box of staticAabbs) {
      // Universal mast ↔ overhead-box test. Runs for every static AABB
      // (bridges, power lines, low cranes…) and early-exits when the
      // box sits below the mast envelope.
      resolveMastBoxCollision(body, box, events);
      // Powerlines have no hull-level geometry — skip the hull SAT pass.
      if (box.kind === "powerline") continue;
      // Only check if the boat's collision-height envelope overlaps the
      // box. If it does, pick the hull layer that best matches that vertical
      // contact band and solve the horizontal overlap with the usual SAT.
      const boatY = boatCollisionWorldYRange(body.boat);
      if (!rangesOverlap(boatY.yMin, boatY.yMax, box.min[1], box.max[1])) {
        continue;
      }
      const profile = selectBoatCollisionProfileForWorldBand(
        body.boat,
        Math.max(boatY.yMin, box.min[1]),
        Math.min(boatY.yMax, box.max[1]),
        !!body.requireCollisionLayerMarkers,
      );
      const boatOutline = getBoatCollisionWorldOutline(body.boat, profile);
      const contact = satConvexPolygons(boatOutline, aabbOutline(box));
      if (!contact) continue;
      const pen = contact.penetration;
      const nx = contact.nx;
      const nz = contact.nz;
      // Baumgarte-style soft stabilization (see shore path). Push out
      // only a fraction of the penetration per frame so contact with a
      // dock face feels like a heavy hull easing against fenders, not a
      // rubber rebound.
      const dockBaumgarte = 0.3;
      const maxDockCorr = 0.15;
      let dockPush = pen * dockBaumgarte;
      if (dockPush > maxDockCorr) dockPush = maxDockCorr;
      body.boat.posX += nx * dockPush;
      body.boat.posZ += nz * dockPush;
      const closing = approachSpeed(body.boat, nx, nz);
      box.onBoatImpact?.({
        boat: body.boat,
        normalX: nx,
        normalZ: nz,
        penetration: pen,
        speed: Math.max(0, closing),
      });
      if (events) {
        if (closing > IMPACT_SPEED_MIN) {
          events.push({
            kind: box.kind ?? "dock",
            boat: body.boat,
            speed: closing,
          });
        }
      }
      killApproach(body.boat, nx, nz);
      // Light contact friction so a boat pinned against a dock by wind
      // doesn't keep skating along it forever.
      body.boat.surge *= 0.92;
      body.boat.sway *= 0.85;
      body.boat.yawRate *= 0.7;
    }
  }

  // Boat ↔ static OBB. Same SAT pipeline as the AABB loop, but the box
  // outline comes from `obbOutline2D` (4 rotated corners) instead of
  // min/max. Mast↔OBB is intentionally skipped — current OBB authors
  // (embankments) sit below mast envelope; add it here if/when a tall
  // OBB obstacle gets introduced.
  for (const body of bodies) {
    if (body.immovable) continue;
    for (const obb of staticObbs) {
      if (obb.kind === "powerline") continue;
      const yr = obbYRange(obb);
      const boatY = boatCollisionWorldYRange(body.boat);
      if (!rangesOverlap(boatY.yMin, boatY.yMax, yr.min, yr.max)) continue;
      const profile = selectBoatCollisionProfileForWorldBand(
        body.boat,
        Math.max(boatY.yMin, yr.min),
        Math.min(boatY.yMax, yr.max),
        !!body.requireCollisionLayerMarkers,
      );
      const boatOutline = getBoatCollisionWorldOutline(body.boat, profile);
      const contact = satConvexPolygons(boatOutline, obbOutline2D(obb));
      if (!contact) continue;
      const pen = contact.penetration;
      const nx = contact.nx;
      const nz = contact.nz;
      const dockBaumgarte = 0.3;
      const maxDockCorr = 0.15;
      let dockPush = pen * dockBaumgarte;
      if (dockPush > maxDockCorr) dockPush = maxDockCorr;
      body.boat.posX += nx * dockPush;
      body.boat.posZ += nz * dockPush;
      const closing = approachSpeed(body.boat, nx, nz);
      obb.onBoatImpact?.({
        boat: body.boat,
        normalX: nx,
        normalZ: nz,
        penetration: pen,
        speed: Math.max(0, closing),
      });
      if (events && closing > IMPACT_SPEED_MIN) {
        events.push({
          kind: obb.kind ?? "dock",
          boat: body.boat,
          speed: closing,
        });
      }
      killApproach(body.boat, nx, nz);
      body.boat.surge *= 0.92;
      body.boat.sway *= 0.85;
      body.boat.yawRate *= 0.7;
    }
  }
}
